'use client';

// Connexion sociale (Google / Facebook) — boutons premium pleine largeur avec
// logos de marque officiels. Démarre le flux OAuth (start → redirection vers le
// fournisseur). Un séparateur « ou » sépare le formulaire e-mail des SSO.

import { useState } from 'react';

import { startOAuth, type OAuthProvider } from '@/api/oauth';
import { Text } from '@/components/ui/Text';
import { useT } from '@/hooks/useT';
import { fontStyle } from '@/theme/typography';

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
      <path fill="#4285F4" d="M23.52 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.87Z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A12 12 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09Z" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75Z" />
    </svg>
  );
}

function FacebookLogo() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
      <path fill="#1877F2" d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.24 2.68.24v2.95h-1.51c-1.49 0-1.95.92-1.95 1.87V12h3.32l-.53 3.47h-2.79v8.38A12 12 0 0 0 24 12Z" />
    </svg>
  );
}

const PROVIDERS: { key: OAuthProvider; Logo: () => React.ReactElement }[] = [
  { key: 'google', Logo: GoogleLogo },
  { key: 'facebook', Logo: FacebookLogo },
];

export function SocialAuthButtons() {
  const { t } = useT('auth');
  const [busy, setBusy] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onProvider = async (provider: OAuthProvider) => {
    setBusy(provider);
    setError(null);
    try {
      const { authorization_url } = await startOAuth(provider);
      window.location.assign(authorization_url);
    } catch {
      setBusy(null);
      setError(t('social.error'));
    }
  };

  return (
    // gap-8 = même rythme que l'espace formulaire→bloc social (parent) : la ligne
    // « ou » est ainsi rigoureusement équidistante du bouton du dessus et des SSO.
    <div className="flex flex-col gap-8">
      {/* Séparateur « ou ». */}
      <div className="flex items-center gap-3" aria-hidden>
        <span className="h-px flex-1 bg-border" />
        <span style={fontStyle('caption')} className="uppercase tracking-wider text-text-tertiary">
          {t('social.divider')}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <div className="flex flex-col gap-3">
        {PROVIDERS.map(({ key, Logo }) => (
          <button
            key={key}
            type="button"
            onClick={() => onProvider(key)}
            disabled={busy !== null}
            style={fontStyle('button')}
            className="rmbr-lift inline-flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-border bg-surface text-text-primary shadow-[var(--rmbr-shadow-sm)] outline-none hover:shadow-[var(--rmbr-shadow-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-action disabled:opacity-60"
          >
            {busy === key ? (
              <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Logo />
            )}
            {t(`social.${key}`)}
          </button>
        ))}
      </div>

      {error ? (
        <Text variant="bodySm" className="text-center text-danger" role="alert">
          {error}
        </Text>
      ) : null}
    </div>
  );
}
